import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import OutletSearch from "../SelectSearch/OutletSearch";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { formatSalesValue } from "../../utils/formatUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiTrash2, FiAlertCircle } from "react-icons/fi";
import ConfirmModal from "../Modal/ConfirmModal";
import { ReceivedOrderModal } from "./OrderHistory/ReceiveOrderModal";
import { formatToISODate } from "../../utils/formateDateUtils";

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ pagination, onPageChange }) => {
    const { current_page, total_pages, total_count, limit } = pagination;
    const [jumpValue, setJumpValue] = useState("");

    const from = Math.min((current_page - 1) * limit + 1, total_count);
    const to = Math.min(current_page * limit, total_count);

    const getPageNumbers = () => {
        const pages = [];
        if (total_pages <= 7) {
            for (let i = 1; i <= total_pages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (current_page > 3) pages.push("...");
        for (let i = Math.max(2, current_page - 1); i <= Math.min(total_pages - 1, current_page + 1); i++) pages.push(i);
        if (current_page < total_pages - 2) pages.push("...");
        pages.push(total_pages);
        return pages;
    };

    const handleJump = (e) => {
        if (e.key === "Enter") {
            const page = parseInt(jumpValue);
            if (page >= 1 && page <= total_pages) { onPageChange(page); setJumpValue(""); }
        }
    };

    return (
        <div className="flex items-center justify-between mt-3 px-1 flex-wrap gap-2">
            <span className="text-sm text-gray-500">Showing {from}–{to} of {total_count} records</span>
            <div className="flex items-center gap-1">
                <button onClick={() => onPageChange(current_page - 1)} disabled={current_page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm">‹</button>
                {getPageNumbers().map((page, idx) =>
                    page === "..." ? (
                        <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm">...</span>
                    ) : (
                        <button key={page} onClick={() => onPageChange(page)}
                            className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${page === current_page ? "bg-blue-600 text-white border border-blue-600" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                            {page}
                        </button>
                    )
                )}
                <button onClick={() => onPageChange(current_page + 1)} disabled={current_page === total_pages}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm">›</button>
                <div className="flex items-center gap-1.5 ml-2">
                    <span className="text-sm text-gray-500">Go to:</span>
                    <input type="number" min={1} max={total_pages} value={jumpValue}
                        onChange={(e) => setJumpValue(e.target.value)} onKeyDown={handleJump}
                        placeholder={current_page}
                        className="w-12 h-8 text-center text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
            </div>
        </div>
    );
};

// ─── Error State ──────────────────────────────────────────────────────────────
const ErrorState = ({ message, onRetry }) => (
    <tr>
        <td colSpan="8" className="py-8 text-center">
            <div className="flex flex-col items-center gap-2 text-red-500">
                <FiAlertCircle size={24} />
                <p className="text-sm font-medium">{message}</p>
                {onRetry && <button onClick={onRetry} className="mt-1 text-xs text-blue-600 hover:underline">Try again</button>}
            </div>
        </td>
    </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderHistory = () => {
    const today = new Date().toLocaleDateString("en-CA");

    const [data, setData] = useState([]);
    const [receivedData, setReceivedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [state, setState] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [pagination, setPagination] = useState({
        current_page: 1,
        limit: 10,
        total_count: 0,
        total_pages: 1,
    });

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const fetchOrders = useCallback(async () => {
        if (!selectedOutlet) return;
        try {
            setLoading(true);
            setError(null);
            const res = await axios.post(`${baseUrl}/ordered_purchase/get_by_outlet`, {
                outlet_name: selectedOutlet,
            });
            setData(res.data || []);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to fetch orders.";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, selectedOutlet]);

    const fetchReceive = useCallback(async (page = 1) => {
        if (!selectedOutlet) return;
        try {
            setLoading(true);
            setError(null);
            const res = await axios.post(`${baseUrl}/getOrderedPurchases`, {
                outlet_name: selectedOutlet,
                state: "Received",
                page,
                token,
                purchase_from: "web",
                from_date: startDate,
                to_date: endDate,
            });
            setReceivedData(res.data?.purchases || []);
            if (res.data?.pagination) setPagination(res.data.pagination);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to fetch orders.";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, selectedOutlet, token, startDate, endDate]);

    useEffect(() => {
        if (state) fetchReceive(1);
        else fetchOrders();
    }, [selectedOutlet, state]);

    const handleSearch = () => {
        if (state) fetchReceive(1);
        else fetchOrders();
    };

    const handlePageChange = (page) => {
        setPagination((prev) => ({ ...prev, current_page: page }));
        fetchReceive(page);
    };

    const getOrderId = (order) =>
        state ? order?.IDIntbl_PurchaseRequisition : order?.IDOrdered_Purchase;

    const handleDeleteClick = (order, e) => {
        e.stopPropagation();
        setOrderToDelete(order);
        setShowConfirmModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!orderToDelete) return;
        const id = getOrderId(orderToDelete);
        setDeleteLoading(id);
        try {
            if (state) {
                await axios.put(`${baseUrl}/deleteinventory_web`, {
                    PurchaseReqID: id,
                    outletName: selectedOutlet,
                });
            } else {
                await axios.post(`${baseUrl}/delete_ordered_purchase`, {
                    ordered_purchase_id: id,
                    outlet_name: selectedOutlet,
                    purchase_from: "web",
                });
            }
            toast.success("Order deleted successfully!");
            if (state) setReceivedData((prev) => prev.filter((o) => getOrderId(o) !== id));
            else setData((prev) => prev.filter((o) => getOrderId(o) !== id));
        } catch (err) {
            toast.error(err?.response?.data?.message || "Delete failed");
        } finally {
            setDeleteLoading(null);
            setShowConfirmModal(false);
            setOrderToDelete(null);
        }
    };

    const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString() : "—");

    const tableData = state ? receivedData : data;

    const mapItemsToForm = (items = []) =>
        items.map((item) => {
            const quantity = parseFloat(item.UnitsOrdered) || 0;
            const rate = parseFloat(item.Rate) || 0;
            const subtotal = quantity * rate;
            const isTaxable = item.Taxable?.toLowerCase() === "yes";
            const vat = isTaxable ? subtotal * 0.13 : 0;
            return {
                ItemName: item.Name || "",
                BrandName: item.BrandName || "",
                quantity,
                primary_unit: item.UOM || "",
                secondary_unit: "",
                Rate: rate,
                Department: item.Department || "",
                GroupName: item.GroupName || "",
                stocktype: item.StockType || "",
                taxable: isTaxable,
                exp_date: item.ExpDate || "",
                subtotal,
                vat,
                total: subtotal + vat,
            };
        });

    const handleRowClick = (order) => {
        if (state) {
            setSelectedOrder(order);
            return;
        }
        if (order.State !== "Ordered") return;
        navigate("/receive-purchase", {
            state: {
                tableItems: mapItemsToForm(order.items),
                companyName: order.Company_Name || "",
                selectedOutlet,
                reqID: order.IDOrdered_Purchase,
                newPurchase: false,
                orderedDate: formatToISODate(order.Date)
            },
        });
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow p-2">
                <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md p-4 mb-2">
                    <h2 className="text-lg font-semibold text-gray-800 mb-3">Purchase History</h2>

                    {/* Controls row */}
                    <div className="flex flex-wrap items-end gap-4 mb-4">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />

                        <div className="flex items-center gap-2 mb-1">
                            <input
                                type="checkbox"
                                checked={state}
                                onChange={(e) => setState(e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="text-sm text-gray-700">View Received</label>
                        </div>

                        {state && (
                            <>
                                {/* Date filters — always visible, relevant especially in received mode */}
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                    />
                                </div>

                                <button
                                    onClick={handleSearch}
                                    disabled={loading}
                                    className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </>
                        )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border rounded-md max-h-[600px] overflow-y-auto tiny-scrollbar">
                        <table className="min-w-full text-sm text-center">
                            <thead className="bg-gray-100 text-gray-700 sticky top-0 z-20">
                                <tr>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Company</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Date</th>
                                    {state ? (
                                        <>
                                            <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">GRN</th>
                                            <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Bill #</th>
                                        </>
                                    ) : (
                                        <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Order ID</th>
                                    )}
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Status</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Items</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Tax</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Total</th>
                                    <th className="px-3 py-2.5 font-semibold uppercase tracking-wider text-xs">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="9" className="text-center py-6 text-gray-400">Loading...</td></tr>
                                ) : error ? (
                                    <ErrorState message={error} onRetry={handleSearch} />
                                ) : tableData.length === 0 ? (
                                    <tr><td colSpan="9" className="text-center py-6 text-gray-400">No records found</td></tr>
                                ) : (
                                    tableData.map((order, idx) => {
                                        const id = getOrderId(order);
                                        const isClickable = state || order.State === "Ordered";

                                        return (
                                            <tr
                                                key={idx}
                                                onClick={() => handleRowClick(order)}
                                                className={`border-t transition ${isClickable ? "hover:bg-blue-50 cursor-pointer" : "opacity-80"}`}
                                            >
                                                <td className="px-3 py-2.5 font-medium text-gray-700">{order.Company_Name}</td>
                                                <td className="px-3 py-2.5 text-gray-600">{formatDate(order.Date)}</td>
                                                {state ? (
                                                    <>
                                                        <td className="px-3 py-2.5 text-gray-600">{order.GRN}</td>
                                                        <td className="px-3 py-2.5 text-gray-600">{order.purchaseBillNumber}</td>
                                                    </>
                                                ) : (
                                                    <td className="px-3 py-2.5 text-gray-600">{id}</td>
                                                )}
                                                <td className="px-3 py-2.5">
                                                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${order.State === "Received" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                                        {order.State}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600">{order.total_items_count ?? "—"}</td>
                                                <td className="px-3 py-2.5 text-gray-600">{formatSalesValue(order.TaxAmount)}</td>
                                                <td className="px-3 py-2.5 font-medium text-gray-800">{formatSalesValue(order.TotalAmount)}</td>
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        onClick={(e) => handleDeleteClick(order, e)}
                                                        disabled={deleteLoading === id}
                                                        className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition"
                                                    >
                                                        {deleteLoading === id ? "..." : <FiTrash2 size={15} />}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {state && pagination.total_pages > 1 && (
                        <Pagination pagination={pagination} onPageChange={handlePageChange} />
                    )}
                </div>
            </div>

            {/* Received order detail modal */}
            {selectedOrder && (
                <ReceivedOrderModal
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                />
            )}

            <ConfirmModal
                isOpen={showConfirmModal}
                title="Confirm Delete"
                message={`Delete order #${getOrderId(orderToDelete)}`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setShowConfirmModal(false)}
                loading={deleteLoading === getOrderId(orderToDelete)}
                danger
            />

            <Footer />
        </div>
    );
};

export default OrderHistory;